import { useState, useEffect } from "react";
import { Airport } from "@/types/airport";
import { useInputValidation } from "@/hooks/use-input-validation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter,
  DialogDescription,
  DialogClose,
} from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

interface AirportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingAirport: Airport | null;
  airports: Airport[];
  onSuccess: () => void;
  onCancel: () => void;
  updateAirport?: (airportId: number, updatedData: Partial<Airport>) => Promise<boolean>;
}

export function AirportDialog({ 
  open,
  onOpenChange,
  editingAirport, 
  airports,
  onSuccess,
  onCancel,
  updateAirport
}: AirportDialogProps) {
  const [dialogKey, setDialogKey] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const nameInput = useInputValidation({
    required: true,
    minLength: 3,
    maxLength: 100,
  });

  const iataInput = useInputValidation({
    required: true,
    pattern: /^[A-Z]{3}$/,
    custom: (value) => !airports.some(airport => 
      airport.iata_code === value && airport.id !== editingAirport?.id
    ),
  });

  const locationInput = useInputValidation({
    required: true,
    minLength: 3,
    maxLength: 100,
  });

  const latitudeInput = useInputValidation({
    required: true,
    pattern: /^-?([0-8]?[0-9]|90)(\.[0-9]{1,6})?$/,
  });

  const longitudeInput = useInputValidation({
    required: true,
    pattern: /^-?((1?[0-7]?|[0-9]?)[0-9]|180)(\.[0-9]{1,6})?$/,
  });

  useEffect(() => {
    if (open) {
      if (editingAirport) {
        console.log("Setting form values from editing airport:", editingAirport);
        nameInput.setValue(editingAirport.name);
        iataInput.setValue(editingAirport.iata_code || "");
        locationInput.setValue(editingAirport.location);
        latitudeInput.setValue(editingAirport.latitude.toString());
        longitudeInput.setValue(editingAirport.longitude.toString());
      } else {
        nameInput.setValue("");
        iataInput.setValue("");
        locationInput.setValue("");
        latitudeInput.setValue("");
        longitudeInput.setValue("");
      }
      nameInput.setError(null);
      iataInput.setError(null);
      locationInput.setError(null);
      latitudeInput.setError(null);
      longitudeInput.setError(null);
    }
  }, [editingAirport, open]);

  const resetForm = () => {
    nameInput.setValue("");
    iataInput.setValue("");
    locationInput.setValue("");
    latitudeInput.setValue("");
    longitudeInput.setValue("");
    setDialogKey(prev => prev + 1);
    onCancel();
  };

  const handleClose = () => {
    resetForm();
    onOpenChange(false);
  };

  const validateInputs = () => {
    const inputs = [nameInput, iataInput, locationInput, latitudeInput, longitudeInput];
    const isValid = inputs.every(input => {
      input.handleBlur();
      return !input.error;
    });

    if (!isValid) {
      toast.error("Please fix the errors in the form");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateInputs() || isSubmitting) return;
    
    setIsSubmitting(true);
    try {
      const airportData = {
        name: nameInput.value,
        iata_code: iataInput.value.toUpperCase(),
        location: locationInput.value,
        latitude: parseFloat(latitudeInput.value),
        longitude: parseFloat(longitudeInput.value),
      };
      
      if (editingAirport) {
        console.log("Updating airport with ID:", editingAirport.id, airportData);
        
        if (updateAirport) {
          const success = await updateAirport(
            editingAirport.id,
            airportData
          );
          
          if (!success) {
            throw new Error("Failed to update airport");
          }
        } else {
          const { error } = await supabase
            .from('airports')
            .update(airportData)
            .eq('id', editingAirport.id);

          if (error) throw error;
        }
        
        toast.success("Airport updated successfully");
      } else {
        console.log("Adding new airport:", airportData);
        const { error } = await supabase
          .from('airports')
          .insert([airportData]);

        if (error) throw error;
        toast.success("Airport added successfully");
      }

      onSuccess();
      handleClose();
    } catch (error: any) {
      console.error("Airport operation failed:", error);
      toast.error(editingAirport ? "Failed to update airport" : "Failed to add airport");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{editingAirport ? 'Edit Airport' : 'Add New Airport'}</DialogTitle>
          <DialogDescription>
            Fill in the airport details below.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <label htmlFor="name">Airport Name *</label>
            <Input
              id="name"
              value={nameInput.value}
              onChange={(e) => nameInput.handleChange(e.target.value)}
              onBlur={nameInput.handleBlur}
              placeholder="Airport name"
            />
            {nameInput.error && nameInput.isTouched && (
              <Alert variant="destructive">
                <AlertDescription>{nameInput.error.message}</AlertDescription>
              </Alert>
            )}
          </div>
          
          <div className="grid gap-2">
            <label htmlFor="iata">IATA Code *</label>
            <Input
              id="iata"
              value={iataInput.value}
              onChange={(e) => iataInput.handleChange(e.target.value.toUpperCase())}
              onBlur={iataInput.handleBlur}
              placeholder="IATA code (e.g., LAX)"
              maxLength={3}
            />
            {iataInput.error && iataInput.isTouched && (
              <Alert variant="destructive">
                <AlertDescription>
                  {iataInput.error.type === 'custom' 
                    ? 'This IATA code is already in use' 
                    : 'IATA code must be exactly 3 uppercase letters'}
                </AlertDescription>
              </Alert>
            )}
          </div>
          
          <div className="grid gap-2">
            <label htmlFor="location">Location *</label>
            <Input
              id="location"
              value={locationInput.value}
              onChange={(e) => locationInput.handleChange(e.target.value)}
              onBlur={locationInput.handleBlur}
              placeholder="City, Country"
            />
            {locationInput.error && locationInput.isTouched && (
              <Alert variant="destructive">
                <AlertDescription>{locationInput.error.message}</AlertDescription>
              </Alert>
            )}
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <label htmlFor="latitude">Latitude *</label>
              <Input
                id="latitude"
                type="text"
                value={latitudeInput.value}
                onChange={(e) => latitudeInput.handleChange(e.target.value)}
                onBlur={latitudeInput.handleBlur}
                placeholder="-90 to 90"
              />
              {latitudeInput.error && latitudeInput.isTouched && (
                <Alert variant="destructive">
                  <AlertDescription>Invalid latitude format</AlertDescription>
                </Alert>
              )}
            </div>
            
            <div className="grid gap-2">
              <label htmlFor="longitude">Longitude *</label>
              <Input
                id="longitude"
                type="text"
                value={longitudeInput.value}
                onChange={(e) => longitudeInput.handleChange(e.target.value)}
                onBlur={longitudeInput.handleBlur}
                placeholder="-180 to 180"
              />
              {longitudeInput.error && longitudeInput.isTouched && (
                <Alert variant="destructive">
                  <AlertDescription>Invalid longitude format</AlertDescription>
                </Alert>
              )}
            </div>
          </div>
        </div>
        
        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={handleClose}
            type="button"
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit}
            disabled={isSubmitting}
            type="button"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {editingAirport ? 'Updating...' : 'Adding...'}
              </>
            ) : (
              editingAirport ? 'Update Airport' : 'Add Airport'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
